import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Sentry from "@sentry/react";
import { Controller, useForm } from "react-hook-form";
import { FirebaseError } from "firebase/app";
import { Link, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { sendPasswordResetEmail } from "firebase/auth";
import { Button, DatePicker, Input } from "@jhool-io/fe-components";
import useToast from "../../../hooks/useToast";
import AuthWrapper from "../components/AuthWrapper/AuthWrapper";
import { IValidateUser } from "../types/auth.types";
import {
    useEncryptAuthFailureEmail,
    useValidateUser,
} from "../hooks/auth.mutations";
import { auth } from "../../../firebase";
import { handleFormatDatePickerValue } from "../../../utils/helpers";

const schema = yup.object().shape({
    user_email: yup
        .string()
        .email("Invalid email")
        .required("Email is required"),
    date_of_birth: yup.string().required("Date of Birth is required"),
});

export default function VerifyIdentityPage() {
    const [dob, setDob] = React.useState<Date | null>(null);
    const navigate = useNavigate();
    const { toast } = useToast();

    const { mutateAsync: encryptEmail } = useEncryptAuthFailureEmail();

    const {
        control,
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<IValidateUser>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const { mutate, isLoading } = useValidateUser();

    const getEncryptedEmailForReset = async (
        email: string,
        reason: string
    ): Promise<string | null> => {
        try {
            const response = await encryptEmail({
                email,
                failure_type: "reset_password",
                metadata: {
                    pp_reason: reason,
                    pp_dob: dob?.toISOString(),
                },
            });
            return response.encrypted_email;
        } catch (encryptError) {
            Sentry.logger.error("Failed to encrypt auth failure email", {
                failure_type: "reset_password",
                reason,
                error: encryptError,
            });
            return null;
        }
    };

    const handleFirebaseErrorToShow = (errorCode: string) => {
        switch (errorCode) {
            case "auth/user-not-found":
                return "No user found with this email address";
            case "auth/invalid-email":
                return "Invalid email address";
            case "auth/too-many-requests":
                return "Too many requests. Please try again later";
            default:
                return `Failed to send reset email`;
        }
    };

    const handleSendPasswordResetEmail = async (email: string) => {
        try {
            await sendPasswordResetEmail(auth, email);
            toast({
                mode: "success",
                message:
                    "Password setup email sent successfully, please check your email",
            });
            const encryptedEmail = await getEncryptedEmailForReset(
                email,
                "Password setup email sent successfully"
            );
            Sentry.logger.info("Password setup email sent successfully", {
                encrypted_email: encryptedEmail,
                failure_type: "reset_password",
            });
            navigate("/login");
        } catch (error) {
            const encryptFailureReason =
                error instanceof FirebaseError
                    ? error.code
                    : "Error sending password reset email";
            const toastMessage =
                error instanceof FirebaseError
                    ? handleFirebaseErrorToShow(error.code)
                    : "Could not send reset email at this time, please try again later";

            toast({
                mode: "error",
                message: toastMessage,
            });

            const encryptedEmail = await getEncryptedEmailForReset(
                email,
                encryptFailureReason
            );
            Sentry.logger.error("Password reset email send failed", {
                encrypted_email: encryptedEmail,
                failure_type: "reset_password",
                reason: encryptFailureReason,
                toast_message: toastMessage,
            });
        }
    };

    const onSubmit = (data: IValidateUser) => {
        const dataToSend = {
            user_email: data.user_email,
            date_of_birth: handleFormatDatePickerValue(data.date_of_birth),
        };
        mutate(dataToSend, {
            onSuccess: async (res) => {
                if (res.is_valid) {
                    await handleSendPasswordResetEmail(data.user_email);
                }
            },
            onError: (error) => {
                toast({
                    mode: "error",
                    message:
                        error.response?.data?.message ||
                        "Email or date of birth doesn't match our records. Please try again or contact support for help.",
                });
            },
        });
    };

    return (
        <AuthWrapper
            footer={
                <>
                    Already have an account?
                    <Link to="/login" className="font-medium text-primary ml-1">
                        Login
                    </Link>
                </>
            }
        >
            <>
                <h2 className="text-xxl font-bold leading-[32px] mb-4">
                    Verify Your Identity
                </h2>
                <p className="text-[#525866] font-normal text-sm mb-40">
                    To access your account, please confirm your identity by
                    entering your registered email address and date of birth.
                    Once verified, an email will be sent to setup your password.
                </p>
                <form className="mt-12" onSubmit={handleSubmit(onSubmit)}>
                    <div className="mb-24">
                        <Input
                            {...register("user_email")}
                            label="Email Address"
                            placeholder="Enter your email address"
                            hasError={!!errors.user_email}
                            errorText={errors.user_email?.message}
                        />
                    </div>
                    <Controller
                        name="date_of_birth"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                selected={dob}
                                onChange={(date) => {
                                    field.onChange(date);
                                    setDob(date);
                                }}
                                maxDate={new Date(Date.now())}
                                hasError={!!errors.date_of_birth}
                                errorText={
                                    errors.date_of_birth?.type === "typeError"
                                        ? "invalid date value"
                                        : errors.date_of_birth?.message
                                }
                                placeholderText="MM/DD/YYYY"
                                label="Date of Birth"
                                className="h-40 text-xs placeholder:text-xs placeholder:text-gray"
                            />
                        )}
                    />

                    <Button
                        className=" w-full mt-32"
                        type="submit"
                        size="medium"
                        disabled={isSubmitting || isLoading}
                    >
                        Send Email
                    </Button>
                </form>
            </>
        </AuthWrapper>
    );
}
